import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { report } from 'api/endpoints/report';
import LayoutMain from 'components/layouts/LayoutMain';
import { OPTION_PAGINATION } from 'constants/index';
import Download from 'pages/Report/Download/Download';
import ReportHeader from 'pages/Report/List/ReportHeader';
import ReportPagination from 'pages/Report/List/ReportPagination';
import ReportSearch from 'pages/Report/List/ReportSearch';
import ReportTable from 'pages/Report/List/ReportTable';
import { convertToQueryString } from 'utils/helper';

const Report = () => {
  const { me } = useSelector((state) => state.auth);
  const { t } = useTranslation();
  const [options, setOptions] = useState({ keyword: null, from_date: null, to_date: null, ...OPTION_PAGINATION });
  const [sort, setSort] = useState([]);
  const [listReports, setListReports] = useState([]);
  const [loading, setLoading] = useState(false);

  const isCallingApi = useRef(false);

  // SEARCH
  const onChangeKeyword = (keyword) => {
    if (keyword !== options.keyword) {
      setOptions((prevState) => {
        return { ...prevState, keyword: keyword };
      });
    }
  };

  // PAGINATION
  const onPageChange = (e) => {
    if (e !== options.page) {
      setOptions((prevState) => {
        return { ...prevState, page: e };
      });
    }
  };

  // SORT
  const onSortChange = (e) => {
    setSort(() => e);
  };

  // DATE
  const onChangeDate = async (date) => {
    if (options.from_date === date.from_date && options.to_date === date.to_date) {
      await getReports();
      return true;
    }
    setOptions((prevState) => {
      return {
        ...prevState,
        ...date,
      };
    });
  };

  /**
   * @name handleSort
   * @returns {string[]}
   * @example ['created_at']
   * @example ['-created_at']
   */
  const handleSort = () => {
    return (
      sort.map((item) => {
        return item.desc ? `-${item.id}` : item.id;
      }) || []
    );
  };

  // GET DATA
  const getReports = useCallback(async () => {
    if (me && !isCallingApi.current) {
      isCallingApi.current = true;
      const sortHandle = handleSort().filter((item) => item !== 'link' && item !== '-link');
      const queryString = convertToQueryString({
        keyword: options.keyword,
        from_date: options.from_date,
        to_date: options.to_date,
        sort_by: sortHandle,
        page: options.page,
      });
      setLoading(true);
      try {
        await report(queryString)
          .then((response) => {
            const totalPage = Math.ceil(response.total / response.page_size);
            const page = response.page > totalPage ? (totalPage === 0 ? 1 : totalPage) : options.page;

            setListReports(response?.data || []);
            setOptions((prevState) => {
              return {
                ...prevState,
                total: response.total,
                page_size: response.page_size,
                page: page,
              };
            });
            setLoading(false);
          })
          .catch(() => {
            setLoading(false);
          });
      } catch {
        setLoading(false);
      } finally {
        isCallingApi.current = false;
      }
    }
  }, [options.page, options.keyword, options.from_date, options.to_date, sort, me]);

  useEffect(() => {
    getReports();
  }, [getReports]);

  return (
    <>
      <LayoutMain>
        <section id="question-list">
          <h1 className="text-2xl font-bold text-black/85">{t('TITLE_REPORT_LIST')}</h1>
          <ReportHeader options={options} onChange={onChangeDate} />
          <ReportSearch options={options} onSubmit={onChangeKeyword} />
          <div className={`w-full mt-4 ${options.total > 0 ? 'min-h-164.75' : ''}`}>
            <ReportTable
              data={listReports}
              setSort={onSortChange}
              reloadData={getReports}
              sort={sort}
              page={options.page}
              keyword={options.keyword}
              fromDate={options.from_date}
              toDate={options.to_date}
              isFilter={!!options.keyword || (options.from_date && options.to_date)}
            />
          </div>
          {options.total > 0 && <ReportPagination options={options} onPageChange={onPageChange} loading={loading} />}
        </section>
      </LayoutMain>
      <Download />
    </>
  );
};

export default React.memo(Report);
